import React from "react";
import ReactDOM from "react-dom";
import { BeatLoader } from "react-spinners";
import styled from "styled-components";
import { theme } from "../configs/theme";
import { PurpleButton } from "../styles/Button.style";
import { Gap } from "../styles/Utils";

type PopupType = "info" | "success" | "error";

interface PopupProps {
  type: PopupType;
  title: string;
  message?: string;
  buttonText: string;
  buttonText2?: string;
  cancelButtonText?: string;
  Icon: React.FC;
  onClose?: () => void;
  action?: () => void;
  action2?: () => void;
  isLoading?: boolean;
}

const Popup: React.FC<PopupProps> = ({
  type,
  title,
  message,
  buttonText,
  buttonText2,
  cancelButtonText,
  Icon,
  onClose,
  action,
  action2,
  isLoading,
}) => {
  const portalRoot = document.getElementById("portal-root");
  if (!portalRoot) return null;

  return ReactDOM.createPortal(
    <Overlay onClick={isLoading ? undefined : onClose}>
      <PopupContainer type={type} onClick={(e) => e.stopPropagation()}>
        <IconWrapper>
          <Icon />
        </IconWrapper>
        <Title type={type}>{title}</Title>
        <Gap $height={1} />
        <Message type={type}>{message}</Message>
        <Gap $height={1} />
        <ActionButton type={type} onClick={action} disabled={isLoading}>
          {isLoading ? <BeatLoader color={type === "info" ? theme.color.tertiaryLight : theme.color.primary} /> : buttonText}
        </ActionButton>
        <Gap $height={0.5} />
        {action2 && (
          <>
            <ActionButton type={type} onClick={action2} disabled={isLoading}>
              {buttonText2}
            </ActionButton>
            <Gap $height={0.5} />
          </>
        )}

        {!isLoading && (
          <TextButton type={type} onClick={onClose}>
            {cancelButtonText}
          </TextButton>
        )}
      </PopupContainer>
    </Overlay>,
    portalRoot
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(200, 200, 200, 0.7); /* Light transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100; /* Ensure it stays on top */
`;

const PopupContainer = styled.div<{
  type: PopupType;
}>`
  position: relative;
  background: ${({ type, theme: { color } }) => (type === "info" ? color.tertiaryLight : color.primaryDark)};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-radius: 30px;
  width: 300px;
  text-align: center;
  border: ${(props) => `3px solid ${props.theme.color.primary}`};
`;

const ActionButton = styled(PurpleButton)<{
  type: PopupType;
}>`
  height: 4rem;
  border-radius: 100px;
  border: none;
  background: ${({ type, theme: { color } }) => (type === "info" ? color.primary : color.primaryExtraLight)};

  color: ${({ type, theme: { color } }) => (type === "info" ? color.tertiaryLight : color.primary)};
`;

const Message = styled.div<{
  type: PopupType;
}>`
  font-size: ${(props) => props.theme.font.size.title.medium};
  color: ${({ type, theme: { color } }) => (type === "info" ? color.primaryDark : color.tertiaryLight)};
  font-family: ${(props) => props.theme.font.family.main};
`;

const TextButton = styled(Message)`
  cursor: pointer;
`;

const Title = styled.label<{ type: "info" | "success" | "error" }>`
  color: ${({ type, theme: { color } }) => {
    const colorMap: Record<"info" | "success" | "error", string> = {
      info: color.tertiary,
      success: color.success,
      error: color.tertiary,
    };
    return colorMap[type] ?? color.tertiary;
  }};
  font-size: ${({ theme }) => theme.font.size.title.big};
  font-family: ${({ theme }) => theme.font.family.title};
  text-shadow: ${({ theme }) => theme.font.elevation.hard.small};
`;

const IconWrapper = styled.div`
  position: absolute;
  padding: 1.2rem;
  top: -70px;
  border-radius: 100px;
  background: ${(props) => props.theme.color.primary};
  height: 6rem;
  width: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    width: 100%;
    height: 100%;
    fill: ${(props) => props.theme.color.tertiaryLight};
  }
`;

export default Popup;
